import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject } from 'rxjs';
import { Organization } from '../../../models/Organization';
import { Logger } from '../../../../../infrastructure/logging/logger';
import { SearchService } from '../../../../../shared/search/search.service';
import { AssignPolicyDialogComponent } from '../../../../policies/components/assign-policy-dialog/assign-policy-dialog.component';
import { ClearPrimaryPolicyDialogComponent } from '../../../../policies/components/clear-primary-policy-dialog/clear-primary-policy-dialog.component';
import { DeletePolicyDialogComponent } from '../../../../policies/components/delete-policy-dialog/delete-policy-dialog.component';
import { PolicyRow, OrganizationPolicyColumns, Policy, PolicyPrice } from '../../../../policies/models/Policy';
import { PolicyService } from '../../../../policies/services/policy.service';
import { PriceRangeModalComponent } from '../../../../price-rates/components/price-range-modal/price-range-modal.component';
import { PriceRatesService } from '../../../../price-rates/services/price-rates.service';
import { Property } from '../../../../properties/models/Property';
import { OrganizationService } from '../../../services/organizations.service';
import { SharedPolicyTableComponent } from '../../../../policies/components/shared-policy-table/shared-policy-table/shared-policy-table.component';

@Component({
    selector: 'app-org-assigned-policies',
    templateUrl: './org-assigned-policies.component.html',
    styleUrls: ['./org-assigned-policies.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      SharedPolicyTableComponent
    ]
})
export class OrgAssignedPoliciesComponent implements OnInit {

    @Input() org!: Organization;
    @Output() loadParent: EventEmitter<any> = new EventEmitter();

    dataSource = new MatTableDataSource<PolicyRow>();
    columns = OrganizationPolicyColumns;
    loading = new BehaviorSubject<boolean>(false);
    tableLength = 0;

    currentOrgId!: any;
    policies: Policy[] = [];
    availablePolicies: Policy[] = [];
    availableSafelyPolicies: Policy[] = [];
    availablePriceRates: PolicyPrice[] = [];
    orgProperties!: Property[];
    propertyAssignedPoliciesArr: Policy[] = [];
    selectedPolicy?: Policy;
    policyToUpdate?: Policy;

    constructor(
        private organizationService: OrganizationService,
        private priceRangeDialog: MatDialog,
        private policyService: PolicyService,
        private priceRateService: PriceRatesService,
        private dialog: MatDialog,
        private logService: Logger,
        private searchService: SearchService
    ) { }

    ngOnInit(): void {
        this.loadPage();
    }

    async loadPage() {
        try {
            this.loading.next(true);
            this.findAvailableSafelyPolicies();
            this.findAvailableSafelyPriceRates();
            this.findPoliciesByOrganizationId(Number(this.org.id));
            this.getOrganizationPropertyPolicies();
        } catch (e) {
            this.logService.handleError(e, 'An error occurred while retrieving Organization Policies', 'Error while retrieving Organization Policies');
        } finally {
            this.loading.next(false);
        }
    }

    async findAvailableSafelyPolicies() {
        this.availableSafelyPolicies = [];
        const response = await this.policyService.getPolicies();
        this.availablePolicies = response.filter(policy => policy.status_id === 1);
        this.availableSafelyPolicies = response.filter(policy => policy.status_id === 1);
    }

    async findPoliciesByOrganizationId(id: number) {
        const policyResponse = await this.organizationService.getPoliciesByOrganizationId(id);
        this.policies = policyResponse.filter(policy => policy.status_id === 1);
        this.populatePolicies();
    }

    populatePolicies() {
        if (this.policies) {
            this.dataSource = new MatTableDataSource(this.policies.map((policy: Policy) => this.policyService.getPolicyRowFromPolicy(policy)));
            this.tableLength = this.dataSource.data.length;
        }
    }

    async findAvailableSafelyPriceRates() {
        this.availablePriceRates = [];
        const response = await this.priceRateService.getPriceRates();
        this.availablePriceRates = response.filter(policy => policy.is_active);
    }

    async getOrganizationPropertyPolicies() {
        const orgPropertyPolicyResponse = await this.organizationService.getAllOrganizationPropertyPolicies();
        const selectedOrgPropertyPolicies = orgPropertyPolicyResponse.filter(pol => pol.organization_id === this.org.id.toString());
        this.propertyAssignedPoliciesArr = this.removeDuplicates(selectedOrgPropertyPolicies, 'insurance_policy_id');
    }

    removeDuplicates(policyArray: any, insurancePolicyIdProperty: any) {
        return policyArray.filter((obj: any, pos: any, arr: any) => {
            return arr.map((mapObj: any) => mapObj[insurancePolicyIdProperty]).indexOf(obj[insurancePolicyIdProperty]) === pos;
        });
    }

    resetSearchQuery() {
        this.searchService.resetQuery();
        this.searchService.setSort("organizationId", -1);
    }

    findPolicyByRow(row: any) {
        let policySelected = undefined;
        policySelected = this.availableSafelyPolicies.find(policy => policy.id === row.insurancePolicyId);
        return policySelected;
    }

    onEditPolicyClick(row: any) {
        this.selectedPolicy = row;
        this.policyToUpdate = this.policies.find(policy => policy.id === row.id);
        if (this.policyToUpdate) {
            this.policyToUpdate.policy_price_id = row.policyPriceId;
            this.onAssignPolicyClick(this.policyToUpdate);
        }
    }

    onAssignPolicyClick(policyToUpdate?: any) {
        const existingOrgPolicyIds = new Set(this.policies.map(policy => policy.insurance_policy_id));
        const eligableAvailableOrgPolicies = this.availablePolicies.filter(availablePolicy => !existingOrgPolicyIds.has(availablePolicy.id));

        const assignDialogRef = this.dialog.open(AssignPolicyDialogComponent, {
            minWidth: '70%',
            maxHeight: '850px',
            disableClose: true,
            autoFocus: false,
            data: {
                component: 'organization-detail',
                availablePolicies: eligableAvailableOrgPolicies,
                availablePriceRates: this.availablePriceRates,
                policyToUpdate: policyToUpdate ? policyToUpdate : []
            }
        });
        assignDialogRef.afterClosed().subscribe((data) => {
            if (data) {
                try {
                    if (this.selectedPolicy) {
                        const organizationUpdatePolicyPayload = this.buildUpdateOranizationPolicy(data, this.selectedPolicy);

                        this.policyService.updatePolicyByComponent(this.selectedPolicy.id, organizationUpdatePolicyPayload, 'organizations').then(_ => {
                            this.findPoliciesByOrganizationId(Number(this.org.id));
                            this.loadParent.emit(this.org.id);
                        });
                    } else {
                        const organizationPolicyPayload = this.buildOrganizationPolicy(data);

                        this.policyService.createPolicyByComponent(organizationPolicyPayload, 'organizations').then(_ => {
                            this.findPoliciesByOrganizationId(Number(this.org.id));
                            this.loadParent.emit(this.org.id);
                        });
                    }
                } catch (e) {
                    this.logService.handleError(e, 'An error occurred while submitting Property Policy', 'Error while submitting Property Policy');
                }
            }
            this.policyToUpdate = undefined;
        });
    }

    buildOrganizationPolicy(data: any) {
        let payload = {
            insurance_policy_id: data.policy.id.toString(),
            organization_id: this.org.id,
            policy_price_id: data.policyPrice,
            status_id: 1
        }
        return payload;
    }

    buildUpdateOranizationPolicy(data: any, selectedPolicy: any) {
        let isPrimary: number = 0;
        if (selectedPolicy.pillStatus === "Primary") {
            isPrimary = 1;
        }

        let payload = {
            account_id: this.selectedPolicy?.account_id,
            id: selectedPolicy.id,
            insurance_policy_id: data.policy.id.toString(),
            is_active: 1,
            is_primary: isPrimary,
            keep_history: 1,
            organization_id: this.org.organization_id.toString(),
            original_id: null,
            policy_price_id: data.policyPrice,
            status_id: 1
        }
        return payload;
    }

    onUpdatePrimaryPolicyClick(row: any) {
        this.selectedPolicy = this.findPolicyByRow(row);
        if (row.pillStatus === 'Primary') {
            this.openUpdatePrimaryPolicyDialog(row.id, false);
        } else {
            this.openUpdatePrimaryPolicyDialog(row.id, true);
        }
    }

    openUpdatePrimaryPolicyDialog(policyId: number, setAsPrimaryPolicy: boolean) {
        const updateDialogRef = this.dialog.open(ClearPrimaryPolicyDialogComponent, {
            width: '60%',
            disableClose: true,
            data: {
                component: 'Policy',
                setAsPrimaryPolicy: setAsPrimaryPolicy
            }
        });
        updateDialogRef.afterClosed().subscribe((setPrimary: any) => {
            if (setPrimary != 'cancel') {
                let payload: any = {
                    id: policyId,
                    is_primary: setPrimary,
                    keep_history: 0
                }
                this.policyService.updatePolicyByComponent(policyId, payload, 'organizations').then(_ => {
                    this.findPoliciesByOrganizationId(Number(this.org.id));
                });
            }
        });
    }

    onDeletePolicyClick(row: any) {
        this.selectedPolicy = this.findPolicyByRow(row);
        this.openDeletePolicyDialog(row);
    }

    openDeletePolicyDialog(policy: Policy) {
        let propertyPolicyExists = this.propertyAssignedPoliciesArr.some(policyCheck => policyCheck.insurance_policy_id === policy.insurance_policy_id);
        const deleteDialogRef = this.dialog.open(DeletePolicyDialogComponent, {
            width: '60%',
            maxHeight: '300px',
            disableClose: true,
            data: {
                component: 'Policy',
                propertyPolicyExists: propertyPolicyExists
            }
        });
        deleteDialogRef.afterClosed().subscribe((data: boolean) => {
            if (data) {
                let payload: any = {
                    id: policy.id,
                    is_primary: 0,
                    is_active: 0,
                    keep_history: 0
                }
                this.policyService.deletePolicyByComponent(policy.id, payload, 'organizations').then(async _ => {
                    this.findPoliciesByOrganizationId(Number(this.org.id));
                });
            }
        });
    }

    getPolicyPrimaryColorForTable(primaryflag: string): string {
        if (primaryflag === "Primary") {
            return '#5cb85c';
        } else {
            return '#ED6C23';
        }
    }

    onViewRangesOpenClick(row: any) {
        this.priceRangeDialog.open(PriceRangeModalComponent, {
            width: '70%',
            data: { row }
        });
    }
}